import { createTranslationHook } from '../i18nFactory';
import { TEXTO_COMUN_ES, TEXTO_COMUN_EN } from '../commonTexts';
import type { TranslateFn } from '../i18nFactory';

/**
 * i18n del widget de Cuotas (FEAT-105-7).
 *
 * Usa `useTranslation` de siesa-ui-kit vía `createTranslationHook`; si el host no
 * registró las claves `installments.*` (el motor devuelve la propia clave), cae al
 * diccionario local del idioma activo y, en último término, al español. Garantiza
 * español listo para usar sin depender de que el documento consumidor registre los
 * recursos. Las claves siguen el formato plano que espera `DefaultTranslationEngine`.
 */

export const INSTALLMENTS_TRANSLATIONS_ES: Record<string, string> = {
  'installments.panel.title': 'Cuotas',
  'installments.panel.empty': 'Este documento no tiene cuotas.',
  // Sin uso: el panel muestra skeleton. Pendiente de confirmar con diseño.
  // Deuda con dueño en docs/i18n-masterdocument-deuda.md (punto 2).
  'installments.panel.loading': 'Cargando cuotas…',
  'installments.panel.loadError': 'No se pudieron cargar las cuotas.',
  'installments.panel.regenerate': 'Regenerar',
  'installments.panel.regenerateAria': 'Regenerar el plan de cuotas',
  'installments.column.number': 'Cuota',
  'installments.column.dueDate': 'Vencimiento',
  'installments.column.promptPaymentDate': 'Fecha pronto pago',
  'installments.column.amount': 'Monto',
  'installments.column.promptPaymentDiscount': 'Descuento PP',
  'installments.column.flags': '',
  'installments.badge.manual': 'Manual',
  'installments.badge.manualTooltip': 'Valor automático: {amount}',
  'installments.edit.amountAria': 'Editar monto de la cuota {number}',
  'installments.modal.title': 'Regenerar cuotas',
  'installments.modal.body': 'Se descartarán los ajustes manuales. ¿Continuar?',
  'installments.modal.cancel': TEXTO_COMUN_ES.cancel,
  'installments.modal.confirm': 'Regenerar',
  'installments.error.amountPositive': 'El monto debe ser mayor o igual a 0.',
  'installments.error.conflict': 'La cuota fue modificada por otro usuario. Se recargó el plan.',
  'installments.error.forbidden': TEXTO_COMUN_ES.errorForbidden,
  'installments.error.tooMany': 'El plan supera el máximo de 36 cuotas.',
  'installments.error.generic': TEXTO_COMUN_ES.errorGeneric,
  'installments.toast.updated': 'Cuota actualizada correctamente.',
  'installments.toast.regenerated': 'Plan de cuotas regenerado.',
};

export const INSTALLMENTS_TRANSLATIONS_EN: Record<string, string> = {
  'installments.panel.title': 'Installments',
  'installments.panel.empty': 'This document has no installments.',
  'installments.panel.loading': 'Loading installments…',
  'installments.panel.loadError': 'Could not load installments.',
  'installments.panel.regenerate': 'Regenerate',
  'installments.panel.regenerateAria': 'Regenerate the installment plan',
  'installments.column.number': 'Installment',
  'installments.column.dueDate': 'Due date',
  'installments.column.promptPaymentDate': 'Prompt payment date',
  'installments.column.amount': 'Amount',
  'installments.column.promptPaymentDiscount': 'PP discount',
  'installments.column.flags': '',
  'installments.badge.manual': 'Manual',
  'installments.badge.manualTooltip': 'Automatic value: {amount}',
  'installments.edit.amountAria': 'Edit amount of installment {number}',
  'installments.modal.title': 'Regenerate installments',
  'installments.modal.body': 'Manual adjustments will be discarded. Continue?',
  'installments.modal.cancel': TEXTO_COMUN_EN.cancel,
  'installments.modal.confirm': 'Regenerate',
  'installments.error.amountPositive': 'The amount must be greater than or equal to 0.',
  'installments.error.conflict': 'The installment was modified by another user. The plan was reloaded.',
  'installments.error.forbidden': TEXTO_COMUN_EN.errorForbidden,
  'installments.error.tooMany': 'The plan exceeds the maximum of 36 installments.',
  'installments.error.generic': TEXTO_COMUN_EN.errorGeneric,
  'installments.toast.updated': 'Installment updated successfully.',
  'installments.toast.regenerated': 'Installment plan regenerated.',
};

/** Diccionarios de respaldo por idioma, listos para registrar en el host si se desea. */
export const INSTALLMENTS_TRANSLATIONS = {
  es: INSTALLMENTS_TRANSLATIONS_ES,
  en: INSTALLMENTS_TRANSLATIONS_EN,
} as const;

/** Función de traducción con interpolación `{var}`. */
export type InstallmentsT = TranslateFn;

/**
 * Hook de traducción del widget.
 *
 * Usa `useTranslation` del kit; si la clave no está registrada en el host
 * (el motor devuelve la propia clave), cae al diccionario local del idioma activo.
 */
export const useInstallmentsTranslation = createTranslationHook(INSTALLMENTS_TRANSLATIONS);
